/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AnyAction, Dispatch } from "redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { AxiosAction } from "order-manager/types/api/common.type"
import { sleep } from "utils/misc"
import { getResponseErrorEventReason } from "utils/tracking"
import { FetchMenuGroupLinkedStoresResponse } from "types/api/menuFetchMenuGroupLinkedStores.type"

export interface Labels {
  LOAD_TIME: number
  LOADING_STATUS: string
  REASON: string
  ERROR_MSG: string
}

export const fetchAllMenuGroupLinkedStoreListFactory = (
  dispatch: Dispatch,
  menuGroupID: string,
  merchantGroupID: string,
) => async () => {
  let page = 1
  let hasMore = false
  const pageSize = 100
  const errors = []
  let storeIDs: string[] = []

  // const t0 = performance.now()
  dispatch(actionCreator.global.showLoading())

  do {
    // eslint-disable-next-line no-await-in-loop
    const resp = (await dispatch(
      actionCreator.menu.fetchMenuGroupLinkedStores({
        menuGroupID,
        merchantGroupID,
        page,
        pageSize,
        append: hasMore,
      }) as AnyAction,
    )) as AxiosAction<FetchMenuGroupLinkedStoresResponse>

    if (resp?.type.endsWith("_FAIL")) {
      const errorEventReason = getResponseErrorEventReason(resp)
      errors.push(errorEventReason)
    } else if ((resp?.payload as any)?.status === 200) {
      storeIDs = [...storeIDs, ...(resp?.payload?.data?.storeIDs ?? [])]
    }

    page += 1
    hasMore = resp?.payload?.data?.hasMore ?? false
    if (!hasMore) {
      break
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(1000)
  } while (hasMore)
}

function useFetchAllMenuGroupLinkedStoreList() {
  const dispatch = useDispatch()
  const {
    menu: {
      menuGroupDetail: { menuGroupID },
      merchantGroupID,
    },
  } = useSelector((s: AppState) => s)
  const fetchAllMenuGroupLinkedStoreList = useMemo(
    () =>
      fetchAllMenuGroupLinkedStoreListFactory(
        dispatch,
        menuGroupID,
        merchantGroupID,
      ),
    [dispatch, menuGroupID, merchantGroupID],
  )
  return fetchAllMenuGroupLinkedStoreList
}

export default useFetchAllMenuGroupLinkedStoreList
